import React, { useState } from "react";
import { ExternalLink, ListOrdered, Settings, UserMinus } from "lucide-react";
import type { CopyTradeSubscription } from "@/api/copy-trade";
import { CopyTradeSubscriptionSettingsForm } from "./CopyTradeSubscriptionSettingsForm";

interface CopyTradeSubscribedTileProps {
  subscription: CopyTradeSubscription;
  onViewDetails: () => void;
  onViewPositions: () => void;
  onUnsubscribe: () => void;
  onSaveSettings: (payload: {
    fixedUsdAmount: number;
    takeProfitPercent?: number | null;
    stopLossPercent?: number | null;
  }) => Promise<void>;
  isSaving?: boolean;
}

const formatPercent = (value?: number | null) =>
  value !== undefined && value !== null ? `${value.toFixed(2)}%` : "—";

const formatSignedUsd = (value: number | null | undefined) => {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return "—";
  }
  const prefix = value >= 0 ? "+" : "";
  return `${prefix}$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const pnlColor = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value) || value === 0) {
    return "text-text-primary";
  }
  return value > 0 ? "text-green-400" : "text-red-400";
};

export const CopyTradeSubscribedTile: React.FC<CopyTradeSubscribedTileProps> = ({
  subscription,
  onViewDetails,
  onViewPositions,
  onUnsubscribe,
  onSaveSettings,
  isSaving,
}) => {
  const snapshot = subscription.leaderSnapshot;
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div className="glass flex flex-col gap-4 p-4">
      <div className="flex items-start gap-3">
        {subscription.leadAvatarUrl ? (
          <img
            src={subscription.leadAvatarUrl}
            alt={subscription.leadNickname}
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-tertiary flex items-center justify-center text-lg font-bold shrink-0">
            {subscription.leadNickname.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-text-primary truncate">
            {subscription.leadNickname}
          </h3>
          <p className="text-xs text-text-secondary">
            Subscribed {new Date(subscription.subscribedAt).toLocaleDateString()}
          </p>
        </div>
        <button
          type="button"
          onClick={onViewDetails}
          className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium text-text-secondary hover:bg-tertiary hover:text-text-primary transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Details
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-text-secondary text-xs">ROI at subscribe</p>
          <p className="font-medium">{formatPercent(snapshot.roi)}</p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">Win rate</p>
          <p className="font-medium">{formatPercent(snapshot.winRate)}</p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">Your margin</p>
          <p className="font-medium">${subscription.fixedUsdAmount} USDT</p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">TP / SL</p>
          <p className="font-medium">
            {subscription.takeProfitPercent ?? "—"}% /{" "}
            {subscription.stopLossPercent ?? "—"}%
          </p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">Your opened positions</p>
          <p className="font-medium">{subscription.mirroredPositionCount}</p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">Average PnL</p>
          <p
            className={`font-medium ${pnlColor(subscription.averageClosedPnl)}`}
          >
            {formatSignedUsd(subscription.averageClosedPnl)}
          </p>
        </div>
      </div>

      {showSettings && (
        <CopyTradeSubscriptionSettingsForm
          fixedUsdAmount={subscription.fixedUsdAmount}
          takeProfitPercent={subscription.takeProfitPercent}
          stopLossPercent={subscription.stopLossPercent}
          isSaving={isSaving}
          onCancel={() => setShowSettings(false)}
          onSave={async (payload) => {
            await onSaveSettings(payload);
            setShowSettings(false);
          }}
        />
      )}

      <div className="flex flex-col gap-2 mt-auto">
        <button
          type="button"
          onClick={onViewPositions}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-tertiary transition-colors"
        >
          <ListOrdered className="w-4 h-4" />
          View positions
        </button>
        {!showSettings && (
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-tertiary transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        )}
        <button
          type="button"
          onClick={onUnsubscribe}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-500/40 text-red-400 text-sm font-medium hover:bg-red-500/10 transition-colors"
        >
          <UserMinus className="w-4 h-4" />
          Unsubscribe
        </button>
      </div>
    </div>
  );
};
